/**
 * useKeyboardScroll - 소프트 키보드 표시 시 포커스된 Input을 가시 영역으로 스크롤
 *
 * 사용법:
 *   const scrollContainerRef = useKeyboardScroll<HTMLDivElement>();
 *   <div ref={scrollContainerRef}> ... inputs ... </div>
 *
 * 동작:
 * 1. keyboardWillShow 이벤트 발생 시 키보드 높이 감지
 * 2. 현재 포커스된 Input이 키보드에 가려지는지 계산
 * 3. 가려지면 scrollIntoView로 보이는 위치로 스크롤
 * 4. 키보드 닫히면 패딩 제거
 */

import { useEffect, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

/** 키보드 위 여백 (px) - Input이 키보드 바로 위에 붙지 않도록 */
const KEYBOARD_PADDING = 16;

/**
 * 스크롤 컨테이너 내 Input이 키보드에 가려지지 않도록 자동 스크롤하는 훅
 * @returns 스크롤 컨테이너에 연결할 ref
 */
export function useKeyboardScroll<T extends HTMLElement>() {
  const containerRef = useRef<T>(null);
  const originalPaddingRef = useRef<string>("");

  const handleKeyboardShow = useCallback((keyboardHeight: number) => {
    const container = containerRef.current;
    if (!container) return;

    // 현재 포커스된 요소
    const activeEl = document.activeElement as HTMLElement | null;
    if (!activeEl || !container.contains(activeEl)) return;

    // 컨테이너에 하단 패딩 추가 (키보드 높이만큼)
    originalPaddingRef.current = container.style.paddingBottom;
    container.style.paddingBottom = `${keyboardHeight + KEYBOARD_PADDING}px`;

    // 포커스된 요소를 가시 영역으로 스크롤
    requestAnimationFrame(() => {
      activeEl.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, []);

  const handleKeyboardHide = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // 패딩 복원
    container.style.paddingBottom = originalPaddingRef.current;
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let showListener: { remove: () => Promise<void> } | undefined;
    let hideListener: { remove: () => Promise<void> } | undefined;

    const setup = async () => {
      showListener = await Keyboard.addListener("keyboardWillShow", (info) => {
        handleKeyboardShow(info.keyboardHeight);
      });
      hideListener = await Keyboard.addListener("keyboardDidHide", () => {
        handleKeyboardHide();
      });
    };

    setup();

    return () => {
      showListener?.remove();
      hideListener?.remove();
    };
  }, [handleKeyboardShow, handleKeyboardHide]);

  return containerRef;
}
